import { type ComponentProps, type ReactNode, useId, useRef } from 'react';

import { cn } from '../cn';
import { Arrows } from '../StrokeIcons';
import type { ColorName, Style, TooltipPosition } from './buttonClasses';
import { composeVariant } from './buttonClasses';
import { inputVariantMutedText } from './buttonClasses';
import Button from './Button';
import FilterPopover from './FilterPopover';
import type { SelectOption } from './Select';
import type { Size, UpdateStatus } from './types';
import { cleanId } from './types';

/** A group of options with a non-selectable header label. */
export type SelectOptionGroup = {
  label: string;
  options: SelectOption[];
};

/** Type guard to check if an option is a group. */
function isOptionGroup(opt: SelectOption | SelectOptionGroup): opt is SelectOptionGroup {
  return 'options' in opt && Array.isArray((opt as SelectOptionGroup).options);
}

type Placement = 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end';

/**
 * Maps a logical placement onto CSS anchor positioning. Forcing an explicit
 * placement (instead of letting positionTryFallbacks flip) avoids a first-
 * paint overlap bug: the browser computes the popover's anchor position
 * before its tall content renders, so flip-block doesn't kick in until the
 * next layout tick, leaving the popover overlapping the trigger.
 */
function placementStyle(placement: Placement | undefined): React.CSSProperties {
  if (!placement) return {};
  const [block, align] = placement.split('-') as ['bottom' | 'top', 'start' | 'end'];
  const blockArea = block === 'bottom' ? 'block-end' : 'block-start';
  const inlineArea = align === 'start' ? 'span-inline-end' : 'span-inline-start';
  return {
    positionArea: `${blockArea} ${inlineArea}`,
    justifySelf: align === 'start' ? 'start' : 'end',
    // Override the default flip fallbacks — placement is intentional.
    positionTryFallbacks: 'none',
  } as React.CSSProperties;
}

export type FilterSelectProps = Omit<ComponentProps<'div'>, 'size' | 'onChange'> & {
  size?: Size;
  /** Color axis — `'blue' | 'red' | 'green' | …`. Defaults to `'gray'`. */
  color?: ColorName;
  /** Style axis — `'filled' | 'outline' | 'soft'`. Defaults to `'filled'`. */
  tone?: Style;
  bordered?: boolean;
  shape?: 'rounded' | 'pill' | 'square';
  icon?: ReactNode;
  rIcon?: ReactNode;
  error?: string;
  /** Options can be flat or include groups with headers. */
  options: (SelectOption | SelectOptionGroup)[];
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  name?: string;
  /** When true, the popup can grow wider than the trigger to fit content. Default: match trigger width. */
  autoWidth?: boolean;
  /** Minimum width for the popover dropdown (px). Default: 200 when autoWidth. */
  popoverMinWidth?: number;
  /** Maximum width for the popover dropdown (px). Default: 400. */
  popoverMaxWidth?: number;
  /**
   * Explicit popover placement relative to the trigger. Default behavior
   * (when undefined) is "below, left-aligned" with auto-flip when there's
   * not enough room. Set this when the trigger lives in a known corner
   * (e.g. bottom-right action bar) — the auto-flip fallback can fire one
   * layout tick late and let the popover overlap the trigger on first open,
   * which is fixed by being explicit.
   */
  popoverPlacement?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end';
  /** Show the filter/search input. Default true. Set false for short lists. */
  filterable?: boolean;
  /** Called when filter input changes. Use for async search with debouncing in parent. */
  onFilterInputChange?: (query: string) => void;
  /** Show loading indicator when fetching async options. */
  isLoadingOptions?: boolean;
  /** Tooltip text shown on hover. */
  tooltip?: ReactNode;
  /** Max width for tooltip in pixels. When set, tooltip text will wrap. */
  tooltipMaxWidth?: number;
  /** Position of the tooltip relative to the button. */
  tooltipPosition?: TooltipPosition;
  /** Status for async operations: 'updating' shows spinner, 'success' shows checkmark, 'error' shows warning. */
  status?: UpdateStatus;
  /** Show the selected label/placeholder text. Default true. Set false for an
   *  icon-only trigger (icon + chevron, no label). */
  showText?: boolean;
  /** Show the dropdown chevron. Default true. */
  showChevron?: boolean;
  /** Called when user wants to create a new item from the filter query. Shows a "Create X" row. */
  onCreate?: (value: string) => void;
  /** Icon for the create row. Defaults to General.PlusSquare. */
  createIcon?: ReactNode;
};

export default function FilterSelect({
  size = 'md',
  bordered = true,
  shape,
  icon,
  rIcon,
  error,
  color,
  tone,
  className,
  disabled,
  options,
  value,
  onChange,
  placeholder,
  name,
  autoWidth,
  popoverMinWidth,
  popoverMaxWidth = 400,
  popoverPlacement,
  filterable,
  onFilterInputChange,
  isLoadingOptions,
  tooltip,
  tooltipMaxWidth,
  tooltipPosition,
  status,
  showText = true,
  showChevron = true,
  onCreate,
  createIcon,
  ...props
}: FilterSelectProps) {
  const id = useId();
  const anchorName = `--filterselect-${cleanId(id)}`;
  const popoverId = `filterselect-popover-${cleanId(id)}`;

  const triggerRef = useRef<HTMLDivElement>(null);

  // Flatten options to find the selected one (handles both flat and grouped)
  const flatOptions = options.flatMap((o: SelectOption | SelectOptionGroup) => (isOptionGroup(o) ? o.options : [o]));
  const selectedOption = flatOptions.find((o: SelectOption) => o.value === value);
  const selectedLabel = selectedOption?.label;
  const triggerIcon = selectedOption?.icon ?? icon;
  // Use selected option's color if it has one, otherwise fall back to component-level color/tone
  const effectiveColor = selectedOption?.color ?? color;
  const effectiveTone = selectedOption?.color != null ? selectedOption.tone : tone;

  // Map options to FilterPopover items (convert SelectOptionGroup to FilterPopoverItemGroup)
  const popoverItems = options.map((o: SelectOption | SelectOptionGroup) =>
    isOptionGroup(o) ? { label: o.label, items: o.options } : o,
  );

  return (
    <div {...props} ref={triggerRef} className={className}>
      {name && <input type="hidden" name={name} value={value ?? ''} />}
      <Button
        type="button"
        size={size}
        bordered={bordered}
        shape={shape}
        color={effectiveColor}
        tone={effectiveTone}
        disabled={disabled}
        error={error}
        inputStyle
        icon={triggerIcon}
        status={status}
        rIcon={showChevron ? (rIcon ?? <Arrows.ChevronDown />) : undefined}
        tooltip={tooltip}
        tooltipMaxWidth={tooltipMaxWidth}
        tooltipPosition={tooltipPosition}
        className={showText ? 'w-full' : undefined}
        popoverTarget={popoverId}
        style={{ anchorName } as unknown as React.CSSProperties}>
        {showText && (
          <span
            className={cn(
              'truncate',
              !selectedLabel && inputVariantMutedText(composeVariant(effectiveColor ?? 'gray', effectiveTone ?? 'filled'), !!error),
            )}>
            {selectedLabel ?? placeholder}
          </span>
        )}
      </Button>

      <FilterPopover
        popoverId={popoverId}
        anchorName={anchorName}
        size={size}
        color={color}
        tone={tone}
        items={popoverItems}
        selectedValue={value}
        onSelect={(val) => onChange?.(val)}
        onOpenChange={(open) => {
          if (!open && document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
          }
        }}
        filterable={filterable}
        onFilterInputChange={onFilterInputChange}
        isLoadingOptions={isLoadingOptions}
        inputIcon={icon}
        onCreate={onCreate}
        createIcon={createIcon}
        popoverStyle={{
          ...(autoWidth
            ? {
                minWidth: popoverMinWidth ?? Math.max(200, triggerRef.current?.offsetWidth ?? 0),
                maxWidth: popoverMaxWidth,
              }
            : { width: triggerRef.current?.offsetWidth }),
          ...placementStyle(popoverPlacement),
        }}
      />
    </div>
  );
}
